import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { DataTable } from '../../components/ui/data-table';
import { Badge } from '../../components/ui/badge';
import { FileUpload } from '../../components/ui/FileUpload';
import { Progress } from '../../components/ui/progress';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../../components/ui/accordion';
import { 
  Upload, 
  RefreshCw, 
  Download,
  FileText,
  CheckCircle,
  XCircle,
  Settings,
  Trash2,
  Edit,
  Save,
} from 'lucide-react';
import { useBOM } from '../../hooks/useBOM';
import { BOMTableColumn } from '../../types/bom';

export function BOMManagement() {
  const [editingItem, setEditingItem] = useState<string | null>(null);
  
  const {
    currentJob,
    currentBOMData,
    isLoading,
    isUploading,
    isExporting,
    extractionParams,
    setExtractionParams,
    submitExtractionJob,
    handleExport,
    handleItemEdit,
    handleItemDelete,
    pollJobStatus,
  } = useBOM();

  const handleFileUpload = (files: File[]) => {
    setExtractionParams({ ...extractionParams, files });
  };

  const handleFileLoad = (loadedFiles: any[]) => {
    const files = loadedFiles.map(loadedFile => {
      if (loadedFile.content instanceof ArrayBuffer) {
        const blob = new Blob([loadedFile.content], { type: loadedFile.type });
        return new File([blob], loadedFile.filename, { type: loadedFile.type });
      } else {
        const blob = new Blob([loadedFile.content], { type: loadedFile.type });
        return new File([blob], loadedFile.filename, { type: loadedFile.type });
      }
    });
    setExtractionParams({ ...extractionParams, files });
  };

  const handleSubmitJob = async () => {
    try {
      await submitExtractionJob(extractionParams);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleExportClick = async (format: 'csv' | 'excel' | 'json' | 'pdf') => {
    if (!currentJob) return;
    
    try {
      await handleExport(format);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const handleItemEditClick = async (item: any) => {
    if (!currentJob) return;
    
    if (editingItem === item.id) {
      try {
        await handleItemEdit(currentJob.job_id, item);
        setEditingItem(null);
      } catch (error) {
        // Error is already handled by the hook
      }
    } else {
      setEditingItem(item.id);
    }
  };

  const handleItemDeleteClick = async (itemId: string) => {
    if (!currentJob) return;
    
    try {
      await handleItemDelete(currentJob.job_id, itemId);
    } catch (error) {
      // Error is already handled by the hook
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="h-3 w-3 mr-1" />Completed</Badge>;
      case 'processing':
        return <Badge className="bg-blue-100 text-blue-800"><RefreshCw className="h-3 w-3 mr-1 animate-spin" />Processing</Badge>;
      case 'failed':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="h-3 w-3 mr-1" />Failed</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800"><RefreshCw className="h-3 w-3 mr-1" />Pending</Badge>;
    }
  };

  // Define columns for BOM data table
  const bomColumns: BOMTableColumn[] = [
    { key: 'part_name', title: 'Part Name', sortable: true, filterable: true, type: 'text' },
    { key: 'description', title: 'Description', sortable: true, filterable: true, type: 'text' },
    { key: 'supplier_name', title: 'Supplier', sortable: true, filterable: true, type: 'text' },
    { key: 'quantity', title: 'Quantity', sortable: true, filterable: true, type: 'number' },
    { key: 'unit_price', title: 'Unit Price', sortable: true, filterable: true, type: 'currency' },
    { key: 'total_price', title: 'Total Price', sortable: true, filterable: true, type: 'currency' },
    { key: 'reference', title: 'Reference', sortable: true, filterable: true, type: 'text' },
    { key: 'source_file', title: 'Source File', sortable: true, filterable: true, type: 'text' },
  ];

  // Convert BOM columns to DataTable format
  const dataTableColumns = bomColumns.map(col => ({
    accessorKey: col.key,
    header: col.title,
    cell: ({ row }: { row: { getValue: (key: string) => any; original: any } }) => {
      const value = row.getValue(col.key);
      const item = row.original;
      
      if (editingItem === item.id) {
        return (
          <input
            type="text"
            defaultValue={value}
            onBlur={(e) => {
              const updatedItem = { ...item, [col.key]: e.target.value };
              handleItemEditClick(updatedItem);
            }}
            className="w-full px-2 py-1 border rounded"
          />
        );
      }

      if (col.type === 'currency' && value) {
        return `$${Number(value).toLocaleString()}`;
      }
      
      if (col.type === 'number' && value) {
        return Number(value).toLocaleString();
      }

      return value || '-';
    },
  }));

  // Add action column
  dataTableColumns.push({
    accessorKey: 'actions',
    header: 'Actions',
    cell: ({ row }: { row: { original: any } }) => {
      const item = row.original;
      return (
        <div className="flex space-x-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditingItem(editingItem === item.id ? null : item.id)}
          >
            {editingItem === item.id ? <Save className="h-4 w-4" /> : <Edit className="h-4 w-4" />}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handleItemDeleteClick(item.id)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      );
    },
  } as any);

  // Poll for job status when there's a current job
  useEffect(() => {
    if (currentJob && (currentJob.status === 'pending' || currentJob.status === 'processing')) {
      const interval = setInterval(() => {
        pollJobStatus(currentJob.job_id);
      }, 3000); // Poll every 3 seconds

      return () => clearInterval(interval);
    }
  }, [currentJob, pollJobStatus]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="md:flex md:items-center md:justify-between">
        <div className="flex-1 min-w-0">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:text-3xl sm:truncate">
            Bill of Materials Management
          </h2>
          <p className="mt-1 text-sm text-gray-500">
            Extract and manage Bill of Materials from various file formats
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="space-y-6">
        {/* Upload Panel */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Upload className="h-5 w-5 mr-2" />
              File Upload
            </CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              accept=".pdf,.csv,.xlsx,.xls,.ods,.jpg,.jpeg,.png,.bmp,.tiff"
              multiple={true}
              maxSize={100}
              onUpload={handleFileUpload}
              onSuccess={handleFileLoad}
              className="mb-4"
            />
          </CardContent>
        </Card>

        {/* Configuration Panel - Accordion */}
        <Card>
          <Accordion type="single" collapsible>
            <AccordionItem value="extraction-config">
              <AccordionTrigger className="px-6">
                <div className="flex items-center">
                  <Settings className="h-5 w-5 mr-2" />
                  Extraction Configuration
                </div>
              </AccordionTrigger>
              <AccordionContent>
                <CardContent className="pt-0">
                  <div className="space-y-4">
                    {/* Extraction Method */}
                    <div className="space-y-2">
                      <Label>Extraction Method</Label>
                      <Select
                        value={extractionParams.extraction_method}
                        onValueChange={(value: 'docling' | 'camelot' | 'chandra') =>
                          setExtractionParams({ ...extractionParams, extraction_method: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="docling">Docling (Recommended)</SelectItem>
                          <SelectItem value="camelot">Camelot</SelectItem>
                          <SelectItem value="chandra">Chandra OCR</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* LLM Provider */}
                    <div className="space-y-2">
                      <Label>LLM Provider</Label>
                      <Select
                        value={extractionParams.llm_provider}
                        onValueChange={(value: 'openai' | 'anthropic') =>
                          setExtractionParams({ ...extractionParams, llm_provider: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="openai">OpenAI</SelectItem>
                          <SelectItem value="anthropic">Anthropic</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Options */}
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="use_llm_mapper"
                          checked={extractionParams.use_llm_mapper}
                          onChange={(e) => setExtractionParams({ ...extractionParams, use_llm_mapper: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="use_llm_mapper" className="text-sm">
                          Use LLM Schema Mapper
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="enable_llm_validation"
                          checked={extractionParams.enable_llm_validation}
                          onChange={(e) => setExtractionParams({ ...extractionParams, enable_llm_validation: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="enable_llm_validation" className="text-sm">
                          Enable LLM Validation
                        </Label>
                      </div>

                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="enable_bom_reasoning"
                          checked={extractionParams.enable_bom_reasoning}
                          onChange={(e) => setExtractionParams({ ...extractionParams, enable_bom_reasoning: e.target.checked })}
                          className="rounded border-gray-300"
                        />
                        <Label htmlFor="enable_bom_reasoning" className="text-sm">
                          Enable BOM Reasoning
                        </Label>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Submit Button */}
        <Button
          onClick={handleSubmitJob}
          disabled={isUploading || extractionParams.files.length === 0}
          className="w-full"
          size="lg"
        >
          {isUploading ? (
            <>
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
              Submitting...
            </>
          ) : (
            <>
              <Upload className="h-4 w-4 mr-2" />
              Submit Extraction Job
            </>
          )}
        </Button>

        {/* Progress Indicator */}
        {currentJob && currentJob.status !== 'completed' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>Extraction Progress</span>
                {getStatusBadge(currentJob.status)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="text-sm text-gray-600">
                  <p><strong>Job ID:</strong> {currentJob.job_id.substring(0, 8)}...</p>
                  <p><strong>Files:</strong> {currentJob.filenames.length} file(s)</p>
                  <p><strong>Method:</strong> {currentJob.extraction_method}</p>
                </div>
                {currentJob.status === 'processing' && (
                  <div className="space-y-2">
                    <Progress value={50} className="w-full" />
                    <p className="text-sm text-gray-600 text-center">Processing your files...</p>
                  </div>
                )}
                {currentJob.status === 'failed' && currentJob.error && (
                  <div className="bg-red-50 border border-red-200 rounded-md p-3">
                    <p className="text-sm text-red-800">{currentJob.error}</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* BOM Data Table */}
        {currentJob && currentJob.status === 'completed' && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  Extracted BOM Data - {currentJob.job_id.substring(0, 8)}...
                </CardTitle>
                <div className="flex space-x-2">
                  <Button
                    variant="outline"
                    onClick={() => handleExportClick('csv')}
                    disabled={isExporting}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export CSV
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleExportClick('excel')}
                    disabled={isExporting}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export Excel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => handleExportClick('json')}
                    disabled={isExporting}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Export JSON
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {isLoading ? (
                <div className="space-y-4">
                  <div className="animate-pulse">
                    <div className="h-10 bg-gray-200 rounded mb-4"></div>
                    <div className="space-y-3">
                      {[1, 2, 3, 4, 5].map((i) => (
                        <div key={i} className="h-12 bg-gray-200 rounded"></div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : currentBOMData.length > 0 ? (
                <DataTable
                  columns={dataTableColumns}
                  data={currentBOMData}
                  searchPlaceholder="Search BOM items..."
                  searchColumn="part_name"
                />
              ) : (
                <div className="text-center py-12">
                  <FileText className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No BOM data found</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    The extraction completed but no BOM items were found.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
